// React
import React, { useEffect, useState } from 'react';

// Packages
import cc from 'classcat';
import t from 'prop-types';
import { useFormContext, useController } from 'react-hook-form';

// Utilities
import { useLabels, useElseware } from 'utilities/hooks';

// Components
import Button from 'components/button';
import Spinner from 'components/spinner';

// Icons
import { FiSearch } from 'react-icons/fi';

const TextLookupComponent = ({
    controller,
    customValidation,
    customValidationError,
    defaultValue,
    disabled,
    display,
    maxLength,
    name,
    object,
    placeholder,
    setValue,
    theme,
    unregisterInput,
    config,
}) => {
    // ///////////////////
    // OBJECT CONFIG
    // ///////////////////

    const { label: inputLabel, subLabel, errorLabel, required } = object || {};

    // ///////////////////
    // HOOKS
    // ///////////////////

    const { label } = useLabels();
    const {
        field: { onChange, value, ref },
        fieldState: { error },
    } = useController({
        name,
        control: controller,
        defaultValue: defaultValue,
        rules: {
            maxLength,
            required,
            validate: {
                customValidation: v =>
                    customValidation ? customValidation(v) : true,
            },
        },
    });
    const { ewCreate } = useElseware();

    const formContext = useFormContext();

    // ///////////////////
    // STATE
    // ///////////////////

    const [lengthValue, setLengthValue] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lookupResult, setLookupResult] = useState(null);
    const [didLookup, setDidLookup] = useState(false);

    // ///////////////////
    // METHODS
    // ///////////////////

    async function doLookup(value) {
        setLoading(true);
        if (value && value.length > 0) {
            // If the value is not empty, perform a lookup
            const lookup = await ewCreate('registry/registry', {
                type: config?.type,
                payload: value.trim().replace(/\s+/g, '').toLowerCase(),
            });

            // Set the lookup result in state
            setLookupResult(lookup);
            setDidLookup(true);
        }
        setLoading(false);
    }

    function resetLookup() {
        setDidLookup(false);
        setLookupResult(null);
        setLoading(false);
    }

    // ///////////////////
    // EFFECTS
    // ///////////////////

    useEffect(() => {
        if (value) {
            setLengthValue(value.length);
        }
    }, []);

    useEffect(() => {
        if (defaultValue) {
            setValue(name, defaultValue);
            setLengthValue(defaultValue.length);
        }
    }, [defaultValue]);

    useEffect(() => {
        if (formContext && unregisterInput) {
            formContext.unregister(name);
        }
    }, [unregisterInput]);

    // ///////////////////
    // THEMING
    // ///////////////////

    const isBlue = theme === 'blue';
    const isBlueAlt = theme === 'blue-alt';
    const isTeal = theme === 'teal';

    // ///////////////////
    // RENDER
    // ///////////////////

    return !unregisterInput ? (
        <>
            <label className="flex flex-col">
                {inputLabel && (
                    <span
                        className={cc([
                            'input-label flex items-center',
                            {
                                'input-label-blue': isBlue,
                                'input-label-blue-alt': isBlueAlt,
                                'input-label-teal': isTeal,
                            },
                        ])}>
                        <FiSearch className="mr-8 -mt-4" size={18} />
                        {inputLabel}{' '}
                        {required && (
                            <span>({label('FormCaptureRequired')})</span>
                        )}
                    </span>
                )}
                {subLabel && (
                    <span
                        className={cc([
                            'mt-8 input-sublabel',
                            {
                                'input-sublabel-blue': isBlue,
                                'input-sublabel-blue-alt': isBlueAlt,
                                'input-sublabel-teal': isTeal,
                            },
                        ])}>
                        {subLabel}
                    </span>
                )}
                {display ? (
                    <div
                        className={cc([
                            'ml-8 mt-4 t-body',
                            {
                                'text-blue-60': isBlue,
                                'text-blue-60': isBlueAlt,
                                'text-teal-60': isTeal,
                            },
                        ])}
                        dangerouslySetInnerHTML={{ __html: value }}
                    />
                ) : (
                    <>
                        <div className="relative w-full">
                            <input
                                ref={ref}
                                type="text"
                                disabled={disabled || display || loading}
                                defaultValue={defaultValue}
                                maxLength={maxLength ? maxLength : 'none'}
                                placeholder={
                                    placeholder ||
                                    label('FormCaptureTextEntryEmpty')
                                }
                                onChange={event => {
                                    const newValue = event.target.value;
                                    // Local value state
                                    setLengthValue(newValue.length);
                                    onChange(newValue);
                                }}
                                className={cc([
                                    'w-full input-defaults',
                                    {
                                        'input-defaults-blue': isBlue,
                                        'input-defaults-blue-alt': isBlueAlt,
                                        'input-defaults-teal': isTeal,
                                        'input-defaults-error': error,
                                        'mt-16': inputLabel,
                                    },
                                ])}
                            />
                            {loading && (
                                <Spinner
                                    theme={theme}
                                    className="absolute right-10 bottom-10"
                                />
                            )}
                        </div>

                        {(maxLength > 0 || customValidationError || error) && (
                            <div className="flex mt-6 -mb-16 input-utility-text">
                                {(error || customValidationError) && (
                                    <div className="input-utility-text-error">
                                        {error?.type === 'customValidation' &&
                                            customValidationError}
                                        {error &&
                                            error?.type !==
                                                'customValidation' &&
                                            errorLabel}
                                    </div>
                                )}
                                {maxLength > 0 && (
                                    <div
                                        className={cc([
                                            'ml-auto text-right',
                                            {
                                                'input-utility-text-blue': isBlue,
                                                'input-utility-text-blue-alt': isBlueAlt,
                                                'input-utility-text-teal': isTeal,
                                            },
                                        ])}>
                                        {lengthValue} / {maxLength.toString()}
                                    </div>
                                )}
                            </div>
                        )}
                    </>
                )}
            </label>
            <div className="flex justify-start !-mt-4 space-x-8">
                <Button
                    variant="secondary"
                    size="tiny"
                    icon={FiSearch}
                    iconType="stroke"
                    theme={theme}
                    action={() => {
                        doLookup(value);
                    }}>
                    {label('ButtonLookup')}
                </Button>
                {didLookup && (
                    <>
                        {lookupResult && (
                            <Button
                                variant="primary"
                                size="tiny"
                                theme={theme}
                                action={() => {
                                    config?.onAccept(lookupResult);
                                    setTimeout(() => {
                                        resetLookup();
                                    }, 600);
                                }}>
                                {label('ButtonAcceptAndFill')}
                            </Button>
                        )}
                        <Button
                            variant="tertiary"
                            size="tiny"
                            theme={theme}
                            tabIndex={-1}
                            action={() => resetLookup()}>
                            {label('ButtonIgnore')}
                        </Button>
                    </>
                )}
            </div>
            {didLookup && (
                <div
                    className={cc([
                        'rounded-4 p-12 !mt-8',
                        {
                            'bg-blue-20 text-blue-80': isBlue,
                            'bg-blue-40 text-blue-80': isBlueAlt,
                            'bg-teal-20 text-teal-80': isTeal,
                        },
                    ])}>
                    {lookupResult ? (
                        <>
                            <div className="flex flex-col space-y-8">
                                <h5 className="!mb-12 t-h6">
                                    {label('InputTextLookupMatchFound')}
                                </h5>
                                {config?.type === 'doi' && (
                                    <>
                                        <p className="flex space-x-12 t-caption">
                                            {lookupResult?.year && (
                                                <span>
                                                    {lookupResult?.year}
                                                </span>
                                            )}
                                            {lookupResult?.publisher && (
                                                <span>
                                                    {lookupResult?.publisher}
                                                </span>
                                            )}
                                            {lookupResult?.publicationType && (
                                                <span>
                                                    {
                                                        lookupResult?.publicationType
                                                    }
                                                </span>
                                            )}
                                        </p>
                                        <p className="t-caption-bold">
                                            {lookupResult?.title}
                                        </p>
                                        <p className="t-caption">
                                            {lookupResult?.authors}
                                        </p>
                                    </>
                                )}
                                {config?.type === 'patent' && (
                                    <>
                                        <p className="t-caption-bold">
                                            {lookupResult?.title} (
                                            {lookupResult?.id})
                                        </p>
                                        <p className="t-caption">
                                            Application date:{' '}
                                            {lookupResult?.applicationDate}
                                            <br />
                                            Publication date:{' '}
                                            {lookupResult?.publicationDate}
                                        </p>
                                    </>
                                )}
                                {config?.type === 'vat' && (
                                    <>
                                        <p className="t-caption-bold">
                                            {lookupResult?.name} (
                                            {lookupResult?.vat})
                                        </p>
                                        <p className="t-caption">
                                            {lookupResult?.address}
                                            <br />
                                            {lookupResult?.city}{' '}
                                            {lookupResult?.zip}
                                        </p>
                                        <p className="t-caption">
                                            {lookupResult?.registrationDate}
                                        </p>
                                    </>
                                )}
                            </div>

                            <p className="mt-16 t-footnote">
                                {label('InputTextLookupAcceptExplainer')}
                            </p>
                        </>
                    ) : (
                        <h5 className="-mb-3 t-sh5">
                            {label('InputTextLookupNoMatchFound')}
                        </h5>
                    )}
                </div>
            )}
        </>
    ) : null;
};

TextLookupComponent.propTypes = {
    controller: t.object.isRequired,
    customValidation: t.func,
    customValidationError: t.string,
    defaultValue: t.string,
    disabled: t.bool,
    display: t.bool,
    maxLength: t.number,
    name: t.string.isRequired,
    object: t.object.isRequired,
    placeholder: t.string,
    setValue: t.func,
    theme: t.oneOf(['teal', 'blue', 'blue-alt']),
    unregisterInput: t.bool,
};

TextLookupComponent.defaultProps = {
    display: false,
    setValue() {},
    theme: 'teal',
};

export default TextLookupComponent;
